// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Alert from '@cloudscape-design/components/alert';
import { AppLayoutProps } from '@cloudscape-design/components/app-layout';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import HelpPanel from '@cloudscape-design/components/help-panel';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Toggle from '@cloudscape-design/components/toggle';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { applyMode, Mode } from '@cloudscape-design/global-styles';

import { adminAuthHeader } from '../../common/admin-token';
import { AdminTokenField } from '../../common/admin-token-field';
import { API_BASE_URL } from '../../common/api-base-url';
import { readCurrentOrg, useCurrentOrg } from '../../common/current-org';
import { readUrlParam, SESSION_EXPIRED_MESSAGE, useAuthToken } from '../../common/locked-identity';
import { ExternalLinkGroup, InfoLink, Notifications } from '../commons';
import { ActionsNavigation, useExpandedNavigation } from '../commons/actions-navigation';
import { CustomAppLayout } from '../commons/common-components';
import logo from '../wizard/logo.svg';
import { mapJsonToWebApplicationState, WebApplicationInput } from './setState';
import {
  DEFAULT_WEB_APP_STATE,
  ENVIRONMENTS,
  IMAGE_PULL_POLICIES,
  NODEPOOLS,
  PROTOCOLS,
  SERVICE_TYPES,
  WebApplicationIntent,
  WebApplicationState,
} from './types';

import '../../styles/base.scss';
import '../../styles/top-navigation.scss';
import '../../styles/wizard.scss';
import '@cloudscape-design/global-styles/index.css';
import '@cloudscape-design/global-styles/dark-mode-utils.css';

interface ToolsContent {
  title: string;
  links: { href: string; text: string }[];
  content: ReactNode;
}

const HELP_CONTENT: Record<string, ToolsContent> = {
  application: {
    title: 'Web application identity',
    links: [],
    content: <p>These values identify where the web application resources are written and deployed.</p>,
  },
  capacity: {
    title: 'Deployment capacity',
    links: [],
    content: (
      <p>
        Resource requests, limits, and scaling values are used to generate the Deployment and autoscaling configuration.
      </p>
    ),
  },
  deployment: {
    title: 'Container settings',
    links: [],
    content: (
      <p>
        These values describe the container image, exposed port, and runtime settings for the Kubernetes Deployment.
      </p>
    ),
  },
  service: {
    title: 'Service exposure',
    links: [],
    content: (
      <p>Service settings map cluster traffic to the Deployment pods. Ingress values are included when enabled.</p>
    ),
  },
};

const i18nStrings = {
  searchIconAriaLabel: 'Search',
  searchDismissIconAriaLabel: 'Close search',
  overflowMenuTriggerText: 'More',
  overflowMenuTitleText: 'All',
  overflowMenuBackIconAriaLabel: 'Back',
  overflowMenuDismissIconAriaLabel: 'Close menu',
};

const profileActions = [
  { id: 'profile', text: 'Profile' },
  { id: 'preferences', text: 'Preferences' },
  { id: 'security', text: 'Security' },
  { id: 'signout', text: 'Sign out' },
];

const signInActions = [{ id: 'signin', text: 'Sign in' }];

const DemoHeaderPortal = ({ children }: { children: ReactNode }) => {
  const domNode = document.querySelector('#h');
  return domNode ? createPortal(children, domNode) : null;
};

const Breadcrumbs = () => (
  <BreadcrumbGroup
    expandAriaLabel="Show path"
    ariaLabel="Breadcrumbs"
    items={[
      { text: 'Platform', href: '#/platform' },
      { text: 'Applications', href: '#/applications' },
      { text: 'Create web application', href: '#/create-web-application' },
    ]}
    onFollow={event => event.preventDefault()}
  />
);

const getFormattedToolsContent = (tools: ToolsContent) => (
  <HelpPanel header={<h2>{tools.title}</h2>} footer={<ExternalLinkGroup items={tools.links} />}>
    {tools.content}
  </HelpPanel>
);

function envVarLinesToObject(value: string) {
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, line) => {
      const [key, ...rest] = line.split('=');
      if (key && rest.length > 0) {
        acc[key.trim()] = rest.join('=').trim();
      }
      return acc;
    }, {});
}

async function getWebApplicationState(orgName: string, applicationName: string, environment: string) {
  const params = new URLSearchParams({
    orgName,
    applicationName,
    environment,
    kind: 'web-application',
  });

  const response = await fetch(`${API_BASE_URL}/api/get-application-state?${params.toString()}`);

  if (!response.ok) {
    return null;
  }

  return (await response.json()) as WebApplicationInput;
}

function numberError(value: string, min: number, max: number) {
  const numeric = Number(value);
  if (!value || Number.isNaN(numeric) || numeric < min || numeric > max) {
    return `Enter a value from ${min} to ${max}.`;
  }
  return undefined;
}

const JsonPreview = ({ intent }: { intent: unknown }) => (
  <Box className="web-app-json-preview">
    <pre>{JSON.stringify(intent, null, 2)}</pre>
  </Box>
);

const App = () => {
  applyMode(Mode.Dark);

  // Which org/app/environment this page targets on load -- from the URL
  // (set by the apps dashboard's "Edit" link, or a Marketplace redirect)
  // when present, falling back to the demo defaults otherwise so nothing
  // already-working changes for the free-text/internal path.
  const [state, setState] = useState<WebApplicationState>(() => {
    // orgName takes priority over org -- see my-apps/root.tsx's editHref/
    // promoteHref comment for why the two must stay distinct (org alone
    // means "Marketplace-locked session" to locked-identity.ts, which this
    // internal navigation is not). org is still checked as a fallback so a
    // genuine Marketplace-redirected session (org=&token=, no orgName=)
    // still prefills correctly.
    const organisationName =
      readUrlParam('orgName') ?? readUrlParam('org') ?? readCurrentOrg() ?? DEFAULT_WEB_APP_STATE.application.organisationName;
    const applicationName = readUrlParam('app') ?? DEFAULT_WEB_APP_STATE.application.applicationName;
    const environmentValue = readUrlParam('environment') ?? String(DEFAULT_WEB_APP_STATE.application.environment.value);
    const environment =
      ENVIRONMENTS.find(option => option.value === environmentValue) ?? DEFAULT_WEB_APP_STATE.application.environment;
    return {
      ...DEFAULT_WEB_APP_STATE,
      application: { ...DEFAULT_WEB_APP_STATE.application, organisationName, applicationName, environment },
    };
  });
  const [searchValue, setSearchValue] = useState('');
  const [toolsContent, setToolsContent] = useState(getFormattedToolsContent(HELP_CONTENT.application));
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadTesting, setIsLoadTesting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<ReactNode>(null);
  const { lockedOrg, lockedToken, authToken, adminToken, setAdminToken } = useAuthToken();
  const { navigationOpen, onNavigationChange } = useExpandedNavigation();
  const { currentOrg, signOut } = useCurrentOrg();
  const appLayoutRef = useRef<AppLayoutProps.Ref>(null);

  useEffect(() => {
    let cancelled = false;

    const loadState = async () => {
      try {
        const restored = await getWebApplicationState(
          state.application.organisationName,
          state.application.applicationName,
          String(state.application.environment.value)
        );

        if (!cancelled && restored) {
          const restoredState = mapJsonToWebApplicationState(restored);
          // promoteTo lets a "Promote to" link (my-apps/root.tsx) fetch state
          // from the SOURCE environment (?environment=) but land the
          // rehydrated form on the promotion TARGET (?promoteTo=), so the
          // user reviews/edits before Submit commits under the target
          // environment. Applied in this same setState call, not a separate
          // update afterwards -- see wizard/root.tsx's identical pattern for
          // why (avoids merging against a stale pre-rehydration closure
          // value under React's batched updates).
          const promoteTo = readUrlParam('promoteTo');
          const targetEnvironment = promoteTo ? ENVIRONMENTS.find(option => option.value === promoteTo) : undefined;
          setState(
            targetEnvironment
              ? { ...restoredState, application: { ...restoredState.application, environment: targetEnvironment } }
              : restoredState
          );
        }
      } catch (error) {
        console.error('Unable to load web application setup state:', error);
      }
    };

    loadState();

    return () => {
      cancelled = true;
    };
  }, []);

  const setHelpPanelContent = (tools: ToolsContent) => {
    setToolsContent(getFormattedToolsContent(tools));
    setIsToolsOpen(true);
    appLayoutRef.current?.focusToolsClose();
  };

  const intent = useMemo<WebApplicationIntent>(
    () => ({
      kind: 'web-application',
      version: 'v1',
      state: {
        ...state,
        deployment: {
          ...state.deployment,
          environment_variables: state.deployment.environment_variables,
        },
      },
    }),
    [state]
  );

  const backendIntent = useMemo(
    () => ({
      ...intent,
      state: {
        ...intent.state,
        deployment: {
          ...intent.state.deployment,
          environment_variables: envVarLinesToObject(intent.state.deployment.environment_variables),
        },
      },
    }),
    [intent]
  );

  const updateSection = <T extends keyof WebApplicationState>(section: T, patch: Partial<WebApplicationState[T]>) => {
    setState(current => ({
      ...current,
      [section]: {
        ...current[section],
        ...patch,
      },
    }));
  };

  const onSubmit = async () => {
    console.log('Submitting web application setup:', backendIntent);
    setIsSubmitting(true);
    setSubmitMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/set-web-application-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeader(authToken ?? '') },
        body: JSON.stringify(backendIntent),
      });

      if (!response.ok) {
        if (response.status === 401 && lockedOrg) {
          setSubmitMessage(<Alert type="error">{SESSION_EXPIRED_MESSAGE}</Alert>);
          return;
        }
        throw new Error('Failed to submit web application setup.');
      }

      setSubmitMessage(<Alert type="success">Web application setup submitted successfully.</Alert>);
    } catch (error) {
      console.error('Error during submission:', error);
      setSubmitMessage(<Alert type="error">Unable to submit the web application setup.</Alert>);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    const confirmed = window.confirm(
      `Delete web application ${state.application.applicationName} from ${state.application.environment.label}?`
    );
    if (!confirmed) {
      return;
    }

    console.log('Submitting web application delete:', backendIntent);
    setIsDeleting(true);
    setSubmitMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/set-web-application-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeader(authToken ?? '') },
        body: JSON.stringify({
          ...backendIntent,
          metadata: {
            ...(backendIntent as any).metadata,
            deleted: true,
          },
        }),
      });

      if (!response.ok) {
        if (response.status === 401 && lockedOrg) {
          setSubmitMessage(<Alert type="error">{SESSION_EXPIRED_MESSAGE}</Alert>);
          return;
        }
        throw new Error('Failed to submit web application delete.');
      }

      setSubmitMessage(<Alert type="success">Web application delete requested successfully.</Alert>);
    } catch (error) {
      console.error('Error during delete submission:', error);
      setSubmitMessage(<Alert type="error">Unable to submit the web application delete request.</Alert>);
    } finally {
      setIsDeleting(false);
    }
  };

  const runLoadTest = async (targetUrl?: string, fallbackLabel = 'the default target') => {
    setIsLoadTesting(true);
    setSubmitMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/web-application/load-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeader(authToken ?? '') },
        body: JSON.stringify({ targetUrl }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401 && lockedOrg) {
          setSubmitMessage(<Alert type="error">{SESSION_EXPIRED_MESSAGE}</Alert>);
          return;
        }
        throw new Error(body?.detail || body?.error || 'Failed to start load test.');
      }

      setSubmitMessage(
        <Alert type="success">Load test started for {body.targetUrl ?? targetUrl ?? fallbackLabel}.</Alert>
      );
    } catch (error) {
      console.error('Error starting load test:', error);
      setSubmitMessage(<Alert type="error">Unable to start the load test.</Alert>);
    } finally {
      setIsLoadTesting(false);
    }
  };

  const onRunLoadTest = async () => {
    const hostname = state.service.hostname || state.service.ingress_host;
    const targetUrl = hostname ? `http://${hostname}` : undefined;
    await runLoadTest(targetUrl);
  };

  const onRunGoHttpbinLoadTest = async () => {
    await runLoadTest(undefined, 'go-httpbin');
  };

  const onStopGoHttpbinLoadTest = async () => {
    setIsLoadTesting(true);
    setSubmitMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/web-application/load-test/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeader(authToken ?? '') },
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401 && lockedOrg) {
          setSubmitMessage(<Alert type="error">{SESSION_EXPIRED_MESSAGE}</Alert>);
          return;
        }
        throw new Error(body?.detail || body?.error || 'Failed to stop load test.');
      }

      setSubmitMessage(<Alert type="success">go-httpbin load test stopped and cleared down.</Alert>);
    } catch (error) {
      console.error('Error stopping load test:', error);
      setSubmitMessage(<Alert type="error">Unable to stop the load test.</Alert>);
    } finally {
      setIsLoadTesting(false);
    }
  };

  return (
    <>
      <DemoHeaderPortal>
        <TopNavigation
          i18nStrings={i18nStrings}
          identity={{
            href: '#',
            title: 'Service name',
            logo: { src: logo, alt: 'Service name logo' },
          }}
          search={
            <Input
              ariaLabel="Input field"
              clearAriaLabel="Clear"
              value={searchValue}
              type="search"
              placeholder="Search"
              onChange={({ detail }) => setSearchValue(detail.value)}
            />
          }
          utilities={[
            {
              type: 'button',
              iconName: 'notification',
              ariaLabel: 'Notifications',
              badge: true,
              disableUtilityCollapse: true,
            },
            { type: 'button', iconName: 'settings', title: 'Settings', ariaLabel: 'Settings' },
            {
              type: 'menu-dropdown',
              text: currentOrg ?? 'Sign in',
              description: currentOrg ? 'Organisation' : 'Not signed in',
              iconName: 'user-profile',
              items: currentOrg ? profileActions : signInActions,
              onItemClick: event => {
                if (event.detail.id === 'signout') {
                  signOut();
                  window.location.href = 'login.html';
                } else if (event.detail.id === 'signin') {
                  window.location.href = 'login.html';
                }
              },
            },
          ]}
        />
      </DemoHeaderPortal>
      <CustomAppLayout
        ref={appLayoutRef}
        navigation={
          <ActionsNavigation
            activeHref="web-app-setup.html"
            lockedOrg={lockedOrg}
            lockedToken={lockedToken}
            webAppSetupExtraItems={[
              { type: 'link', text: 'Run go-httpbin load test', href: '#/run-go-httpbin-load-test' },
              { type: 'link', text: 'Stop go-httpbin load test', href: '#/stop-go-httpbin-load-test' },
            ]}
            onFollow={event => {
              if (event.detail.href === '#/run-go-httpbin-load-test') {
                event.preventDefault();
                onRunGoHttpbinLoadTest();
              } else if (event.detail.href === '#/stop-go-httpbin-load-test') {
                event.preventDefault();
                onStopGoHttpbinLoadTest();
              }
            }}
          />
        }
        navigationOpen={navigationOpen}
        onNavigationChange={onNavigationChange}
        breadcrumbs={<Breadcrumbs />}
        tools={toolsContent}
        toolsOpen={isToolsOpen}
        onToolsChange={event => setIsToolsOpen(event.detail.open)}
        contentType="form"
        notifications={<Notifications bannerContent="Creates the resources and configuration for a web application." />}
        content={
          <div className="platform-watermark-content">
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link">Cancel</Button>
                  <Button loading={isDeleting} disabled={isSubmitting || isDeleting} onClick={onDelete}>
                    Delete web app
                  </Button>
                  <Button variant="primary" loading={isSubmitting} onClick={onSubmit}>
                    Create web app
                  </Button>
                </SpaceBetween>
              }
              header={<Header variant="h1">Create web application</Header>}
            >
              <SpaceBetween size="l">
                {readUrlParam('promoteTo') && (
                  <Alert type="info" header="Reviewing a promotion">
                    Loaded from {readUrlParam('environment')}. Review and edit as needed -- submitting will save this
                    as {state.application.environment.label}, not overwrite the source environment.
                  </Alert>
                )}
                {submitMessage}
                {!lockedOrg && <AdminTokenField value={adminToken} onChange={setAdminToken} />}
                <Container
                  header={
                    <Header
                      variant="h2"
                      info={<InfoLink onFollow={() => setHelpPanelContent(HELP_CONTENT.application)} />}
                    >
                      Application details
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <ColumnLayout columns={2}>
                      <FormField
                        label="Organisation name"
                        description={lockedOrg ? 'Locked to your Marketplace-linked organisation.' : undefined}
                      >
                        <Input
                          value={state.application.organisationName}
                          placeholder="example-organisation"
                          disabled={Boolean(lockedOrg)}
                          onChange={event => updateSection('application', { organisationName: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Application name">
                        <Input
                          value={state.application.applicationName}
                          placeholder="new-web-app"
                          onChange={event => {
                            const value = event.detail.value;
                            updateSection('application', { applicationName: value });
                            updateSection('deployment', { containers_name: value });
                            updateSection('service', {
                              name: `${value}-service`,
                              hostname: `${value}.ncoda.ai`,
                            });
                          }}
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField label="Environment">
                        <Select
                          selectedOption={state.application.environment}
                          options={ENVIRONMENTS}
                          selectedAriaLabel="Selected"
                          onChange={event => updateSection('application', { environment: event.detail.selectedOption })}
                        />
                      </FormField>
                      <FormField label="Namespace">
                        <Input
                          value={state.application.namespace}
                          placeholder="tcf-applications"
                          onChange={event => updateSection('application', { namespace: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>

                <Container
                  header={
                    <Header
                      variant="h2"
                      info={<InfoLink onFollow={() => setHelpPanelContent(HELP_CONTENT.deployment)} />}
                    >
                      Deployment
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <ColumnLayout columns={3}>
                      <FormField label="Container name">
                        <Input
                          value={state.deployment.containers_name}
                          placeholder="new-web-app"
                          onChange={event => updateSection('deployment', { containers_name: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Image repository">
                        <Input
                          value={state.deployment.containers_image}
                          placeholder="registry.example.com/new-web-app"
                          onChange={event => updateSection('deployment', { containers_image: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Image tag">
                        <Input
                          value={state.deployment.containers_tag}
                          placeholder="v0.0.1"
                          onChange={event => updateSection('deployment', { containers_tag: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={3}>
                      <FormField
                        label="Container port"
                        errorText={numberError(state.deployment.container_port, 1, 65535)}
                      >
                        <Input
                          type="number"
                          value={state.deployment.container_port}
                          onChange={event => {
                            updateSection('deployment', { container_port: event.detail.value });
                            updateSection('service', { target_port: event.detail.value });
                          }}
                        />
                      </FormField>
                      <FormField label="Replicas" errorText={numberError(state.deployment.replicas, 1, 100)}>
                        <Input
                          type="number"
                          value={state.deployment.replicas}
                          onChange={event => updateSection('deployment', { replicas: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Image pull policy">
                        <Select
                          selectedOption={state.deployment.image_pull_policy}
                          options={IMAGE_PULL_POLICIES}
                          selectedAriaLabel="Selected"
                          onChange={event =>
                            updateSection('deployment', { image_pull_policy: event.detail.selectedOption })
                          }
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField label="Service account name">
                        <Input
                          value={state.deployment.service_account_name}
                          placeholder="new-web-app-sa"
                          onChange={event => updateSection('deployment', { service_account_name: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Environment variables" description="One KEY=value pair per line.">
                        <Textarea
                          value={state.deployment.environment_variables}
                          rows={3}
                          placeholder="LOG_LEVEL=info"
                          onChange={event => updateSection('deployment', { environment_variables: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>

                <Container
                  header={
                    <Header
                      variant="h2"
                      info={<InfoLink onFollow={() => setHelpPanelContent(HELP_CONTENT.capacity)} />}
                    >
                      Capacity
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <ColumnLayout columns={2}>
                      <FormField label="Nodepool">
                        <Select
                          selectedOption={state.details.nodepool}
                          options={NODEPOOLS}
                          selectedAriaLabel="Selected"
                          onChange={event => updateSection('details', { nodepool: event.detail.selectedOption })}
                        />
                      </FormField>
                      <FormField
                        label="Target CPU utilization"
                        errorText={numberError(state.details.scale_target_utilization, 1, 100)}
                      >
                        <Input
                          type="number"
                          value={state.details.scale_target_utilization}
                          onChange={event => updateSection('details', { scale_target_utilization: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField
                        label="Request CPU"
                        errorText={numberError(state.details.resources_requests_cpu, 1, 64)}
                      >
                        <Input
                          type="number"
                          value={state.details.resources_requests_cpu}
                          onChange={event => updateSection('details', { resources_requests_cpu: event.detail.value })}
                        />
                      </FormField>
                      <FormField
                        label="Request memory"
                        errorText={numberError(state.details.resources_requests_memory, 1, 128)}
                      >
                        <Input
                          type="number"
                          value={state.details.resources_requests_memory}
                          onChange={event =>
                            updateSection('details', { resources_requests_memory: event.detail.value })
                          }
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField label="Limit CPU" errorText={numberError(state.details.resources_limits_cpu, 1, 128)}>
                        <Input
                          type="number"
                          value={state.details.resources_limits_cpu}
                          onChange={event => updateSection('details', { resources_limits_cpu: event.detail.value })}
                        />
                      </FormField>
                      <FormField
                        label="Limit memory"
                        errorText={numberError(state.details.resources_limits_memory, 1, 256)}
                      >
                        <Input
                          type="number"
                          value={state.details.resources_limits_memory}
                          onChange={event => updateSection('details', { resources_limits_memory: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField
                        label="Minimum replicas"
                        errorText={numberError(state.details.scale_min_replicas, 1, 100)}
                      >
                        <Input
                          type="number"
                          value={state.details.scale_min_replicas}
                          onChange={event => updateSection('details', { scale_min_replicas: event.detail.value })}
                        />
                      </FormField>
                      <FormField
                        label="Maximum replicas"
                        errorText={numberError(state.details.scale_max_replicas, 1, 100)}
                      >
                        <Input
                          type="number"
                          value={state.details.scale_max_replicas}
                          onChange={event => updateSection('details', { scale_max_replicas: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField
                        label="Requests per second threshold"
                        errorText={numberError(state.details.scale_rps_threshold, 1, 100000)}
                      >
                        <Input
                          type="number"
                          value={state.details.scale_rps_threshold}
                          onChange={event => {
                            updateSection('details', { scale_rps_threshold: event.detail.value });
                            updateSection('service', { rps_threshold: event.detail.value });
                          }}
                        />
                      </FormField>
                    </ColumnLayout>
                  </SpaceBetween>
                </Container>

                <Container
                  header={
                    <Header
                      variant="h2"
                      info={<InfoLink onFollow={() => setHelpPanelContent(HELP_CONTENT.service)} />}
                      actions={
                        <Button loading={isLoadTesting} onClick={onRunLoadTest}>
                          Run load test
                        </Button>
                      }
                    >
                      Service
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <ColumnLayout columns={3}>
                      <FormField label="Service name">
                        <Input
                          value={state.service.name}
                          placeholder="new-web-app-service"
                          onChange={event => updateSection('service', { name: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Service type">
                        <Select
                          selectedOption={state.service.type}
                          options={SERVICE_TYPES}
                          selectedAriaLabel="Selected"
                          onChange={event => updateSection('service', { type: event.detail.selectedOption })}
                        />
                      </FormField>
                      <FormField label="Protocol">
                        <Select
                          selectedOption={state.service.protocol}
                          options={PROTOCOLS}
                          selectedAriaLabel="Selected"
                          onChange={event => updateSection('service', { protocol: event.detail.selectedOption })}
                        />
                      </FormField>
                    </ColumnLayout>
                    <ColumnLayout columns={2}>
                      <FormField label="Service port" errorText={numberError(state.service.port, 1, 65535)}>
                        <Input
                          type="number"
                          value={state.service.port}
                          onChange={event => updateSection('service', { port: event.detail.value })}
                        />
                      </FormField>
                      <FormField label="Target port" errorText={numberError(state.service.target_port, 1, 65535)}>
                        <Input
                          type="number"
                          value={state.service.target_port}
                          onChange={event => updateSection('service', { target_port: event.detail.value })}
                        />
                      </FormField>
                    </ColumnLayout>
                    <FormField label="Route hostname">
                      <Input
                        value={state.service.hostname}
                        placeholder="new-web-app.ncoda.ai"
                        onChange={event => {
                          updateSection('service', {
                            hostname: event.detail.value,
                            ingress_host: event.detail.value,
                          });
                        }}
                      />
                    </FormField>
                    <Toggle
                      checked={state.service.ingress_enabled}
                      onChange={event => updateSection('service', { ingress_enabled: event.detail.checked })}
                    >
                      Enable ingress
                    </Toggle>
                    {state.service.ingress_enabled && (
                      <ColumnLayout columns={2}>
                        <FormField label="Ingress host">
                          <Input
                            value={state.service.ingress_host}
                            placeholder="payments.example.com"
                            onChange={event => updateSection('service', { ingress_host: event.detail.value })}
                          />
                        </FormField>
                        <FormField label="Ingress path">
                          <Input
                            value={state.service.ingress_path}
                            placeholder="/"
                            onChange={event => updateSection('service', { ingress_path: event.detail.value })}
                          />
                        </FormField>
                      </ColumnLayout>
                    )}
                  </SpaceBetween>
                </Container>

                <Container header={<Header variant="h2">Backend payload</Header>}>
                  <JsonPreview intent={backendIntent} />
                </Container>
              </SpaceBetween>
            </Form>
          </div>
        }
      />
    </>
  );
};

export default App;
